import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertActionLink,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  PageSection,
  Skeleton,
  Stack,
  StackItem,
} from '@patternfly/react-core';
import { ChartPie } from '@patternfly/react-charts/victory';
import { LockIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { formatDateTime } from '../../../utils/dateFormat';
import type { CertificateBucketKey } from '../../../types/models/Certificate';
import type { CertificateStats } from '../../../types/models/CertificatePlatform';
import type { ApiResponseCertificateStats } from '../../../types/api/ApiResponseCertificatePlatform';
import {
  CERTIFICATE_BUCKET_CHART_COLORS,
  CERTIFICATE_BUCKET_LABELS,
  CERTIFICATE_BUCKET_ORDER,
} from './certificateBuckets';

function CertificatesOverview() {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const [stats, setStats] = useState<CertificateStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setStats(null);
    setError(null);
    try {
      const response = await apiFetch('/datacenters/certificates/stats');
      let body: ApiResponseCertificateStats | null;
      try {
        body = (await response.json()) as ApiResponseCertificateStats;
      } catch {
        body = null;
      }
      if (!response.ok || !body || body.status !== 'success' || !body.data?.stats) {
        const message = body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
        setError(message);
        return;
      }
      setStats(body.data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected network error occurred.');
    }
  }, [apiFetch]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  const navigateToPlatformBucket = useCallback(
    (platformId: number, bucket: CertificateBucketKey) => {
      const params = new URLSearchParams({
        tab: String(platformId),
        status: bucket,
      });
      navigate(`/datacenter/certificates?${params.toString()}`);
    },
    [navigate],
  );

  // Pie data, legend data, and colour scale derived together so the slice
  // order, legend rows, and colours always line up. Zero-count buckets stay
  // in the array — Victory renders them as zero-extent (invisible) slices,
  // so the legend still shows all 6 statuses with their proper colours
  // while the visible slices are only those with data.
  const pieData = useMemo(
    () =>
      stats === null
        ? []
        : CERTIFICATE_BUCKET_ORDER.map((bucket) => ({
            x: CERTIFICATE_BUCKET_LABELS[bucket],
            y: stats.buckets[bucket],
          })),
    [stats],
  );

  const pieLegendData = useMemo(
    () =>
      stats === null
        ? []
        : CERTIFICATE_BUCKET_ORDER.map((bucket) => ({
            name: `${CERTIFICATE_BUCKET_LABELS[bucket]}: ${stats.buckets[bucket].toLocaleString('fr-BE')}`,
          })),
    [stats],
  );

  const pieColorScale = useMemo(
    () => CERTIFICATE_BUCKET_ORDER.map((bucket) => CERTIFICATE_BUCKET_CHART_COLORS[bucket]),
    [],
  );

  if (error !== null) {
    return (
      <PageSection hasBodyWrapper={false}>
        <Alert
          variant="danger"
          isInline
          title="Failed to load certificate dashboard"
          actionLinks={<AlertActionLink onClick={() => void fetchStats()}>Retry</AlertActionLink>}
        >
          {error}
        </Alert>
      </PageSection>
    );
  }

  if (stats === null) {
    return (
      <PageSection hasBodyWrapper={false}>
        <Stack hasGutter>
          <StackItem>
            <Skeleton width="220px" screenreaderText="Loading certificate dashboard" />
          </StackItem>
          <StackItem>
            <Skeleton height="120px" width="100%" />
          </StackItem>
          <StackItem>
            <Skeleton height="260px" width="100%" />
          </StackItem>
          <StackItem>
            <Skeleton height="200px" width="100%" />
          </StackItem>
        </Stack>
      </PageSection>
    );
  }

  return (
    <PageSection hasBodyWrapper={false}>
      <Stack hasGutter>
        <StackItem>
          <Content component="small" className="pf-v6-u-color-200">
            Last updated {formatDateTime(stats.generated_at)}
          </Content>
        </StackItem>

        <StackItem>
          <Card>
            <CardTitle>
              <span className="pf-v6-u-font-size-3xl">{stats.total.toLocaleString('fr-BE')}</span>
            </CardTitle>
            <CardBody>
              <Content component="p" className="pf-v6-u-color-200">
                Total certificates across {stats.platform_count} {stats.platform_count === 1 ? 'platform' : 'platforms'}
              </Content>
            </CardBody>
          </Card>
        </StackItem>

        <StackItem>
          <Card>
            <CardTitle>By status</CardTitle>
            <CardBody>
              {stats.total === 0 ? (
                <Content component="p" className="pf-v6-u-color-200">
                  No certificates yet — the breakdown will appear here once the first one syncs.
                </Content>
              ) : (
                <div style={{ height: '260px', maxWidth: '600px' }}>
                  <ChartPie
                    ariaTitle="Certificates by status"
                    ariaDesc="Distribution of certificates across status buckets"
                    constrainToVisibleArea
                    data={pieData}
                    legendData={pieLegendData}
                    legendOrientation="vertical"
                    legendPosition="right"
                    colorScale={pieColorScale}
                    labels={({ datum }) => `${datum.x}: ${datum.y}`}
                    height={260}
                    width={600}
                    padding={{
                      top: 20,
                      bottom: 20,
                      left: 20,
                      right: 200,
                    }}
                    name="certificates-by-status-pie"
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </StackItem>

        <StackItem>
          <Content component="h2" className="pf-v6-u-font-size-xl">
            By platform
          </Content>
        </StackItem>

        <StackItem>
          {stats.platforms.length === 0 ? (
            <EmptyState titleText="No certificate platforms available" icon={LockIcon} variant="lg" headingLevel="h3">
              <EmptyStateBody>Contact Ancalagon to enable certificate management for your organization.</EmptyStateBody>
            </EmptyState>
          ) : (
            <Grid hasGutter>
              {stats.platforms.map((platform) => {
                const platformPieData = CERTIFICATE_BUCKET_ORDER.map((bucket) => ({
                  x: CERTIFICATE_BUCKET_LABELS[bucket],
                  y: platform.buckets[bucket],
                }));
                return (
                  <GridItem key={platform.platform_id} span={12} lg={6}>
                    <Card isFullHeight>
                      <CardTitle>
                        <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
                          <FlexItem>{platform.platform_name}</FlexItem>
                          <FlexItem>
                            {platform.clientID === null ? (
                              <Label color="blue" isCompact>
                                Shared
                              </Label>
                            ) : (
                              <Label color="grey" isCompact>
                                Dedicated
                              </Label>
                            )}
                          </FlexItem>
                        </Flex>
                      </CardTitle>
                      <CardBody>
                        <Stack hasGutter>
                          <StackItem>
                            <Content component="small" className="pf-v6-u-color-200">
                              <code>{platform.platform_type}</code>
                              {' · '}
                              {/* ?? null: a backend predating last_synced_at omits the
                                  key, and formatDateTime only em-dashes null */}
                              Last synced {formatDateTime(platform.last_synced_at ?? null)}
                            </Content>
                          </StackItem>
                          <StackItem>
                            {platform.total === 0 ? (
                              <Content component="p" className="pf-v6-u-color-200">
                                No certificates yet on this platform.
                              </Content>
                            ) : (
                              <Flex
                                alignItems={{ default: 'alignItemsCenter' }}
                                spaceItems={{ default: 'spaceItemsLg' }}
                                flexWrap={{ default: 'wrap' }}
                              >
                                <FlexItem>
                                  <div style={{ height: '180px', width: '180px' }}>
                                    <ChartPie
                                      ariaTitle={`Certificates by status on ${platform.platform_name}`}
                                      ariaDesc="Distribution of certificates across status buckets"
                                      constrainToVisibleArea
                                      data={platformPieData}
                                      colorScale={pieColorScale}
                                      labels={({ datum }) => `${datum.x}: ${datum.y}`}
                                      height={180}
                                      width={180}
                                      padding={10}
                                      name={`certificates-pie-platform-${platform.platform_id}`}
                                    />
                                  </div>
                                </FlexItem>
                                <FlexItem flex={{ default: 'flex_1' }}>
                                  <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsSm' }}>
                                    {CERTIFICATE_BUCKET_ORDER.map((bucket) => (
                                      <FlexItem key={bucket}>
                                        <Button
                                          variant="link"
                                          isInline
                                          aria-label={`View ${CERTIFICATE_BUCKET_LABELS[bucket]} certificates on ${platform.platform_name}`}
                                          onClick={() => navigateToPlatformBucket(platform.platform_id, bucket)}
                                        >
                                          <span
                                            aria-hidden="true"
                                            style={{
                                              display: 'inline-block',
                                              width: '10px',
                                              height: '10px',
                                              borderRadius: '2px',
                                              backgroundColor: CERTIFICATE_BUCKET_CHART_COLORS[bucket],
                                              marginRight: '8px',
                                              verticalAlign: 'middle',
                                            }}
                                          />
                                          {CERTIFICATE_BUCKET_LABELS[bucket]}:{' '}
                                          {platform.buckets[bucket].toLocaleString('fr-BE')}
                                        </Button>
                                      </FlexItem>
                                    ))}
                                  </Flex>
                                </FlexItem>
                              </Flex>
                            )}
                          </StackItem>
                        </Stack>
                      </CardBody>
                    </Card>
                  </GridItem>
                );
              })}
            </Grid>
          )}
        </StackItem>
      </Stack>
    </PageSection>
  );
}

export default CertificatesOverview;
